import axios, { AxiosError, AxiosResponse } from "axios";
import { detect } from "detect-browser";
import { throwError, failed_login } from "./navigationWorker";
import { LogError } from "../actions/errorWorker";
import * as querystring from "querystring-es3";
import { get_choice_field_dependence, has_capability } from "../actions/entityWorker";
import { DropdownMenuItemType } from "@fluentui/react";
import { v4 as uuidv4 } from "uuid";
import { ApiResponseData, AppConfig } from "../types";
import { getAppConfig } from "../appConfig";

export const attachnmentTimeout =
	"Due to the large size of your email, this email will be filed in the background. Please check in Xplan to confirm successful filing of the email.";

const ERROR_MESSAGES = {
	"The remote server returned an error: (401) This feature is not available without a valid session.":
		"Session Expired. Please log back in.",
	"Error converting the response object of type System.Object from the Lambda function to JSON: Unable to expand length of this stream beyond its capacity.":
		attachnmentTimeout,
	"The remote server returned an error: (500) error deserializing result.":
		"The remote server returned an error: (500) error deserializing result.",
	'The remote server returned an error: (500) Schema node "choice_category_dependence": Required.':
		'The remote server returned an error: (500) Schema node "choice_category_dependence": Required.',
};

export function formatURL(url: string): string {
	if (url && url.endsWith("/")) {
		return url.slice(0, -1);
	}
	return url;
}

export function formatDate(date: Date): string {
	const monthNames = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

	const day = date.getDate();
	const monthIndex = date.getMonth();
	const year = date.getFullYear();

	return `${year}-${monthNames[monthIndex]}-${day}`;
}

export function formatDateTime(date: Date): string {
	const hour = date.getHours();
	const minute = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();

	return `${formatDate(date)} ${hour}:${minute}`;
}

export function setXplanURL(url: string): void {
	try {
		localStorage.setItem("xplan_url", url);
	} catch (error) {
		throwError(error);
		return null;
	}
}

export function getXplanURL(): string {
	return formatURL(localStorage.getItem("xplan_url") || "");
}

export function setALBXplanCookie(val: string) {
	if (val.indexOf("XPLANID=") < 0) {
		const xplan_cookie = localStorage.getItem("xplan_cookie");
		const xplan_index = xplan_cookie.indexOf("XPLANID=");
		return `${val}${xplan_cookie.substring(xplan_index)}`;
	}

	return val;
}

export function setXplanCookie(value: string) {
	let tfaCookie = value.split(";").find((c) => c.startsWith("tfa-"));
	if (tfaCookie) {
		tfaCookie = tfaCookie += ";";
		localStorage.setItem("tfa_cookie", tfaCookie);
	}
	localStorage.setItem("xplan_cookie", value);
	const _1day = 24 * 60 * 60 * 1000;
	localStorage.setItem("xplan_cookie_expire", String(Date.now() + _1day));
}

export function getXplanCookie(): string {
	const e = new Date();
	if (e.getTime() < Number(localStorage.getItem("xplan_cookie_expire"))) {
		return localStorage.getItem("xplan_cookie");
	} else {
		localStorage.removeItem("xplan_cookie");
		localStorage.removeItem("xplan_cookie_expire");
		return null;
	}
}

export function getAuthType(): string {
	return localStorage.getItem("auth_token_type") || "Basic";
}

export function getoAuthData(): Record<string, string> {
	return {
		access_token: localStorage.getItem("access_token"),
		refresh_token: localStorage.getItem("refresh_token"),
		auth_token_type: localStorage.getItem("auth_token_type"),
	};
}

export function getHeaders(): object {
	const headers = {
		"Access-Control-Allow-Origin": "*",
		"Content-Type": "application/json",
	};
	if (localStorage.getItem("access_token")) {
		headers["Authorization"] = `bearer ${localStorage.getItem("access_token")}`;
	}
	return headers;
}

export async function getHeadersAsync(): Promise<object> {
	// Check if token needs refresh before making API call
	if (shouldRefreshToken()) {
		console.log("[Auth] Token needs refresh before API call");
		await refreshAccessToken();
	}
	
	return getHeaders();
}

export function isEmpty(val: string) {
	return !val || val.trim().length === 0;
}

export function base64encode(str: string) {
	if (isEmpty(str)) return str;
    // Convert to binary string manually to avoid regex
    const binary = Array.from(new TextEncoder().encode(str))
        .map((code) => String.fromCharCode(code))
        .join("");
	return btoa(binary);
}

export function getPayload(url: string, auth = null, method = "GET") {
	const payload: { [key: string]: any } = {
		url,
		auth,
		method,
		data: {},
		cookie: getXplanCookie(),
		target: "XPLAN",
		source: "Outlook",
		user_id: getLoginUserId(),
		user_name: "",
		display_name: getDisplayName(),
		user_story_session_guid: getUserStorySessionGuid(),
		bucket_name: appConfig().bucket_name,
		aws_region: appConfig().awsRegion,
		environment: appConfig().environment,
		gmail_payload: {},
		...getLogData(),
	};

	if (getAuthType() !== "Basic") {
		payload["auth_type"] = getAuthType();
		payload["auth_data"] = getoAuthData();
	}

	return payload;
}

export async function getPayloadAsync(url: string, auth = null, method = "GET") {
	// Check if token needs refresh before making API call
	if (shouldRefreshToken()) {
		console.log("[Auth] Token needs refresh before building payload");
		await refreshAccessToken();
	}
	
	return getPayload(url, auth, method);
}

export function getLogData() {
	const browser = detect();
	const version = "0.0.0.0";
	const data: { [key: string]: any } = {
		is_shared_mailbox: isSharedMailbox(),
		is_desktop: Office?.context?.diagnostics?.platform
			? Office.context.diagnostics.platform !== Office.PlatformType.OfficeOnline
			: false,
		is_sso: localStorage.getItem("is_sso_login") === "true",
		office_platform: Office?.context?.mailbox?.diagnostics?.hostName || "",
		outlook_version: Office?.context?.mailbox?.diagnostics?.hostVersion || version,
		user_name: localStorage.getItem("user_name"),
	};

	if (browser) {
		data.browser_name = browser.name;
		data.browser_version = browser.version;
		data.os = browser.os;
	}

	return data;
}

export function isSharedMailbox() {
	if (!Office?.context?.mailbox?.item) return false;

	try {
		return !!Office.context.mailbox.item.getSharedPropertiesAsync;
	} catch {
		return false;
	}
}

export const APICalls = {
	Outlook: "https://outlook.office365.com/api/v2.0/me/messages",
	User: "/resourceful/session/user",
	EntitySearch: "/resourceful/entity/",
	DocnoteProperties: "/resourceful/docnote-v2/category",
	FiledNotes: "/resourceful/docnote-v5?page_size=100&page_sort=-created_date{2}",
	ActiveTasks:
		"/resourceful/task-v2?statuses.0=1000&statuses.1=2000&statuses.2=2500&statuses.3=3000{2}&list_option=all",
	FileEmail: "/resourceful/docnote-v2",
	FileEmailtoTask: "/resourceful/task-v2",
	Ufieldsearch: "/resourceful/ufield/",
	Capabilities: "/resourceful/session/capability",
	AWSUrl: (): string => {
		const result = appConfig();
		return result.aws_url || "";
	},
	AdviserSearch: "/resourceful/entity/user-v2?",
	FileToURL: "/resourceful/sysadmin/outlook_plugin",
	LogError: "/resourceful/logging/app",
	ExistingDocNote: "/resourceful/extdocnotelink",
	XplanVersion: "/resourceful/site",
	UserMessage: "/resourceful/user_message",
	LogoutXplan: "/resourceful/session",
	UserFieldDefaults: "/resourceful/sysadmin/outlook_plugin_settings",
	Oauth2Token: "/oauth2/token",
	MicrosoftGraph: "https://graph.microsoft.com",
};

export function IsOverlayShown(): boolean {
	return document.body.contains(document.getElementById("overlay"));
}

const mailboxErrors = ["ErrorMailboxMoveInProgress", "ErrorInvalidRequest", "ErrorItemNotFound", "ErrorNonExistentMailbox"];

export function handleResponseData(
	response: AxiosResponse<ApiResponseData>,
	no_data_message: string,
	suppress_error = false
) {
	try {
		if (response?.data?.isError === true) {
			if (["Unauthorized", "401"].includes(response.data.statuscode)) {
				if (ERROR_MESSAGES[response.data.message]) {
					failed_login(ERROR_MESSAGES[response.data.message]);
					return null;
				}

				if (response.data.message.includes("TFA required"))
					return { message: "TFA Required", IsTFA: true };

				failed_login(response.data.message);
				return null;
			}

			if (response.data.statuscode === "InternalServerError") {
				LogError(response.data, {}, "", "ERROR");
				return response.data;
			}

			if (response.data.statuscode === "500" && ERROR_MESSAGES[response.data.message]) {
				LogError(response.data, {}, "", "ERROR");
				return null;
			}

			if (
				response.data.statuscode === "500" &&
				response.data.message.includes(
					'The remote server returned an error: (500) Schema node "'
				)
			) {
				LogError(response.data, {}, "", "ERROR");
				return {
					isError: true,
					message:
						"One or more Type/Subtype fields no longer exist. Please correct in Xplan.",
				};
			}

			if (response.data.statuscode === "404") {
				failed_login(response.data.message);
				return response.data;
			}

			if (mailboxErrors.includes(response.data.statuscode)) {
				LogError(response.data, {}, "MailboxError", "ERROR");
				return response.data;
			}

			const message = response.data.message || response.data.errorMessage;
			if (message) return { message, isError: true };

			LogError(response.data, {}, "Unhandled Error", "ERROR");
			return null;
		}

		if (response?.data?.data) {
			let parsed_data: any;
			try {
				parsed_data = JSON.parse(response.data.data);
			} catch (e) {
				parsed_data = response.data.attachments;
			}

			if (parsed_data.length === 0) {
				if (suppress_error) return null;
				throw new Error(no_data_message);
			}
			return parsed_data;
		}

		if (response?.data?.errorMessage) {
			LogError(response, {}, "handleResponseData", "ERROR");
			const msg = ERROR_MESSAGES[response.data.errorMessage];

			if (msg === attachnmentTimeout) {
				return { message: attachnmentTimeout, isError: false };
			}

			if (msg) throw new Error(msg);
			throw new Error(response.data.errorMessage);
		}

		if (response?.data) return response.data;

		return null;
	} catch (error) {
		if (suppress_error) return null;
		throwError(error);
	}
}

export function getQueryParams(): querystring.ParsedUrlQuery {
	return querystring.parse(window.location.search.substring(1));
}

export const EntityTypesMap = (from) => {
	const types = {
		client: "client-v2",
		user: "user",
		referral: "referrer",
		supplier: "supplier",
		profadviser: "profadviser",
	};
	return types[from];
};

export function getEntityTypes(
	additional: Array<{ key: string; text: string; itemType: DropdownMenuItemType }>
): Array<{ key: string; text: string }> {
	const result = [
		{ key: "client-v2", text: "Client" },
		{ key: "user", text: "User" },
		{ key: "referrer", text: "Referrer" },
		{ key: "supplier", text: "Supplier" },
		{ key: "profadviser", text: "Professional Adviser" },
	];

	for (const add in additional) {
		if (add) {
			result.unshift(additional[add]);
		}
	}
	return result;
}

export function getEntityTypeText(key: string): string {
	key = key === "referral" ? "referrer" : key;
	key = key === "client" ? "client-v2" : key;
	const item = getEntityTypes(null).find((x) => {
		return x.key === key;
	});
	if (!item) {
		return "Client";
	}
	return item.text;
}

export function GetDependOptions(
	groupName: string,
	parentValue: string
): Array<{ key: string; text: string }> {
	get_choice_field_dependence(groupName, "subtype", "");
	const data = JSON.parse(localStorage.getItem(groupName + "_subtype_results")) || [];
	const mapping = JSON.parse(localStorage.getItem(groupName + "_subtype_depend_results")) || "";

	if (mapping?.choice_category_dependence) {
		const dependOptions = mapping.choice_category_dependence;
		if (
			dependOptions !== null &&
			dependOptions[parentValue] !== null &&
			dependOptions[parentValue] !== undefined
		) {
			const subOptions = dependOptions[parentValue];
			const result = [];
			data.filter((x) => subOptions.includes(x[0].toString())).map((z) =>
				result.push({ key: z[0], text: z[1] })
			);
			return result;
		}

		const result = [];
		data.map((z) => result.push({ key: z[0], text: z[1] }));
		return result;
	}

	const result = [];
	data.map((z) => result.push({ key: z[0], text: z[1] }));
	return result;
}

export function GetItemText(listName: string, key: string): string {
	const data = JSON.parse(localStorage.getItem(listName)) || [];

	try {
		return data.find((x) => x[0].toString() === key)[1];
	} catch (error) {
		return "";
	}
}

export function SetPermissionList(): void {
	const permissions = [];
	if (has_capability("docnote_shareprivate")) {
		permissions.push(["0", "Private"]);
	}
	if (has_capability("docnote_sharemygroups")) {
		permissions.push(["1", "Shared With My Groups"]);
	}
	if (has_capability("docnote_shareall")) {
		permissions.push(["2", "Shared With All"]);
	}
	if (has_capability("docnote_sharespecgroups")) {
		permissions.push(["3", "Shared With Specific Groups"]);
	}
	if (has_capability("docnote_shareclientgroups")) {
		permissions.push(["4", "Shared With Client Groups"]);
	}
	localStorage.setItem("PermissionList", JSON.stringify(permissions));
}

export function getLoginUserId(): string {
	return localStorage.getItem("loginUserId") || "";
}

export function getDisplayName(): string {
	return localStorage.getItem("loginUserName") || "";
}

export function getLoginUserName(): string {
	return localStorage.getItem("xplan_user_name") || "";
}

export function getAppClientID(): string {
	const result = appConfig();
	return result.app_client_id;
}

export function getRedirectURL(): string {
	const result = appConfig();
	return result.redirect_url;
}

export function getXplanTokenURL(): string {
	return getXplanURL() + APICalls.Oauth2Token;
}

export function getEnvironment(): string {
	return appConfig().environment;
}

export function trimChar(origString: string, charToTrim: string): string {
    let start = 0;
    while (start < origString.length && charToTrim.indexOf(origString[start]) >= 0) {
        start++;
    }

    let end = origString.length - 1;
    while (end >= start && charToTrim.indexOf(origString[end]) >= 0) {
        end--;
    }

    return origString.substring(start, end + 1);
}

export const httpMethods = {
	Get: "GET",
	Post: "POST",
	Patch: "PATCH",
	Delete: "DELETE",
};

export function appConfig(): AppConfig {
	const config = localStorage.getItem("config");
	return config ? JSON.parse(config) : getAppConfig();
}

export function setConfig(config: object): void {
	localStorage.setItem("config", JSON.stringify(config));
}

export function setAxiosDefaults(): void {
	axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
	axios.defaults.headers.common["Content-Type"] = "application/json";
}

export function watchForAuthCode(): void {
	let pollCount = 0;
	let timerId: NodeJS.Timeout | null = null;
	let isPolling = false;
	console.log("[SSO Poll] Starting watchForAuthCode polling with exponential backoff");
	
	// Exponential backoff intervals: 500ms, 1s, 2s, 3s, 4s, then 5s max
	const getNextInterval = (count: number): number => {
		if (count === 1) return 500;   // First retry: 500ms
		if (count === 2) return 1000;  // Second retry: 1s
		if (count === 3) return 2000;  // Third retry: 2s
		if (count === 4) return 3000;  // Fourth retry: 3s
		if (count === 5) return 4000;  // Fifth retry: 4s
		return 5000;                   // After 5th: 5s max
	};
	
	const pollForTokens = () => {
		if (isPolling) {
			console.log("[SSO Poll] Skipping poll - previous request still in progress");
			return;
		}
		
		const uss_guid = localStorage.getItem("user_story_session_guid");
		if (!uss_guid) {
			console.log("[SSO Poll] No session GUID found, stopping polling");
			return;
		}
		
		pollCount++;
		isPolling = true;
		
		// Log every 5th poll to reduce noise
		if (pollCount % 5 === 1 || pollCount <= 3) {
			console.log(`[SSO Poll ${pollCount}] Checking for tokens - Session: ${uss_guid}, Next interval: ${getNextInterval(pollCount)}ms`);
		}
		
		const payload = getPayload("", null, httpMethods.Post);
		payload.target = "GetSSOTokens";
		payload.user_story_session_guid = uss_guid;
		
		axios
			.post(APICalls.AWSUrl(), payload)
			.then((res) => {
				isPolling = false;
				
				if (res.data && res.data.isError === false && res.data.data) {
					try {
						let tokenData;
						if (typeof res.data.data === "string") {
							tokenData = JSON.parse(res.data.data);
						} else {
							tokenData = res.data.data;
						}
						
						console.log(`[SSO Success] Found auth tokens after ${pollCount} polls`);
						console.log(`[SSO Success] Token data:`, {
							has_access_token: !!tokenData.access_token,
							has_refresh_token: !!tokenData.refresh_token,
							token_type: tokenData.token_type,
							expires_in: tokenData.expires_in
						});
						
						// Store all tokens
						if (tokenData.auth_code !== null && tokenData.auth_code !== undefined) {
							localStorage.setItem("auth_code", tokenData.auth_code);
						}
						if (tokenData.access_token !== null && tokenData.access_token !== undefined) {
							localStorage.setItem("access_token", tokenData.access_token);
						}
						if (tokenData.refresh_token !== null && tokenData.refresh_token !== undefined) {
							localStorage.setItem("refresh_token", tokenData.refresh_token);
						}
						if (tokenData.token_type !== null && tokenData.token_type !== undefined) {
							localStorage.setItem("auth_token_type", tokenData.token_type);
						}
						if (tokenData.expires_in !== null && tokenData.expires_in !== undefined) {
							localStorage.setItem("expires_in", tokenData.expires_in);
							const expiryTime = Date.now() + (parseInt(tokenData.expires_in) - 300) * 1000;
							localStorage.setItem("access_token_expiry", expiryTime.toString());
						}
						
						// Start automatic token refresh timer
						if (tokenData.refresh_token) {
							startTokenRefreshTimer();
							console.log("[SSO] Token refresh timer started after successful authentication");
						}
						
						// Stop polling immediately
						if (timerId) {
							clearTimeout(timerId);
							timerId = null;
						}
						
						// For SSO, directly proceed to validate session instead of full page reload
						console.log("[SSO] Tokens stored successfully, validating session");
						setTimeout(async () => {
							try {
								const { check_valid_cookie } = await import("../actions/navigationWorker");
								const { formatURL, getXplanURL, APICalls } = await import("../actions/helper");
								const url = getXplanURL() + APICalls.User;
								await check_valid_cookie(formatURL(url), true);
							} catch (error) {
								console.error("[SSO] Error during session validation:", error);
								// Fallback to reload if direct validation fails
								location.reload();
							}
						}, 100);
						
					} catch (parseError) {
						console.error(`[SSO Error] Failed to parse token data:`, parseError);
						scheduleNextPoll();
					}
				} else {
					// Tokens not ready, schedule next poll with backoff
					scheduleNextPoll();
				}
			})
			.catch((err) => {
				isPolling = false;
				
				if (!err.response) {
					console.error(`[SSO Error] Network error:`, err.message);
					stopPolling();
				} else if (err.response.status !== 404 && err.response.status !== 400) {
					console.error(`[SSO Error] HTTP ${err.response.status} error:`, err.response.data);
					stopPolling();
				} else {
					// 404/400 = tokens not ready, continue with backoff
					scheduleNextPoll();
				}
			});
	};
	
	const scheduleNextPoll = () => {
		if (pollCount >= 50) { // Max ~2 minutes with exponential backoff
			console.log("[SSO Poll] Max polls reached, stopping");
			stopPolling();
			return;
		}
		
		const nextInterval = getNextInterval(pollCount);
		timerId = setTimeout(pollForTokens, nextInterval);
	};
	
	const stopPolling = () => {
		if (timerId) {
			clearTimeout(timerId);
			timerId = null;
		}
		console.log("[SSO Poll] Polling stopped");
	};
	
	// Start immediately (first poll has no delay)
	console.log("[SSO Poll] Starting first poll immediately");
	pollForTokens();
}

export async function refreshAccessToken(): Promise<boolean> {
	const refresh_token = localStorage.getItem("refresh_token");
	const xplan_url = getXplanURL();
	
	if (!refresh_token || !xplan_url) {
		console.log("[Refresh Token] No refresh token or xplan URL available");
		return false;
	}

	console.log("[Refresh Token] Attempting to refresh access token");

	try {
		const tokenUrl = xplan_url + "/oauth2/token";
		const payload = getPayload(tokenUrl, null, httpMethods.Post);
		payload.data["client_id"] = getAppClientID();
		payload.data["redirect_uri"] = getRedirectURL();
		payload.data["grant_type"] = "refresh_token";
		payload.data["refresh_token"] = refresh_token;
		payload.target = "XPLANSSO";

		const response = await axios.post(APICalls.AWSUrl(), payload);
		const data = handleResponseData(response, "");

		if (data && data.access_token) {
			console.log("[Refresh Token] Successfully refreshed access token");
			localStorage.setItem("access_token", data.access_token);
			
			// Update refresh token if a new one was provided
			if (data.refresh_token) {
				localStorage.setItem("refresh_token", data.refresh_token);
			}
			
			if (data.token_type) {
				localStorage.setItem("auth_token_type", data.token_type);
			}
			
			if (data.expires_in) {
				localStorage.setItem("expires_in", data.expires_in);
				// Set expiry time (current time + expires_in - 5 minutes buffer)
				const expiryTime = Date.now() + (parseInt(data.expires_in) - 300) * 1000;
				localStorage.setItem("access_token_expiry", expiryTime.toString());
			}
			
			return true;
		} else {
			console.error("[Refresh Token] No access token in response");
			return false;
		}
	} catch (error) {
		console.error("[Refresh Token] Error refreshing token:", error);
		LogError(error, { refresh_token: "***" }, "refreshAccessToken", "ERROR");
		return false;
	}
}

export function shouldRefreshToken(): boolean {
	const access_token = localStorage.getItem("access_token");
	const refresh_token = localStorage.getItem("refresh_token");
	const expiry = localStorage.getItem("access_token_expiry");
	
	// No tokens = no refresh needed
	if (!access_token || !refresh_token) {
		return false;
	}
	
	// If no expiry time set, refresh after 55 minutes (conservative)
	if (!expiry) {
		const expires_in = localStorage.getItem("expires_in");
		if (expires_in) {
			// Assume token was just issued, set expiry for 55 minutes from now
			const expiryTime = Date.now() + (parseInt(expires_in) - 300) * 1000;
			localStorage.setItem("access_token_expiry", expiryTime.toString());
			return false;
		}
		// Default: assume 1 hour token, check if we should refresh
		return true;
	}
	
	// Check if token has expired or will expire soon (within 5 minutes)
	const expiryTime = parseInt(expiry);
	const now = Date.now();
	const timeUntilExpiry = expiryTime - now;
	
	if (timeUntilExpiry <= 0) {
		console.log("[Refresh Token] Access token has expired");
		return true;
	}
	
	if (timeUntilExpiry < 5 * 60 * 1000) { // Less than 5 minutes
		console.log(`[Refresh Token] Access token expiring soon (${Math.round(timeUntilExpiry / 60000)} minutes)`);
		return true;
	}
	
	return false;
}

export function startTokenRefreshTimer(): void {
	// Clear any existing timer first to prevent duplicates
	const existingTimer = (window as Window & { _tokenRefreshInterval?: NodeJS.Timeout })._tokenRefreshInterval;
	if (existingTimer) {
		clearInterval(existingTimer);
		console.log("[Refresh Token Timer] Cleared existing timer before starting new one");
	}
	
	// Check every 2 minutes if token needs refresh
	const checkInterval = setInterval(async () => {
		if (shouldRefreshToken()) {
			console.log("[Refresh Token Timer] Token needs refresh");
			const success = await refreshAccessToken();
			
			if (!success) {
				console.error("[Refresh Token Timer] Failed to refresh token, clearing login");
				clearInterval(checkInterval);
				// Clear login and force re-authentication
				clearLogin();
				location.reload();
			}
		}
	}, 2 * 60 * 1000); // Check every 2 minutes
	
	// Store interval ID so it can be cleared if needed
	(window as Window & { _tokenRefreshInterval?: NodeJS.Timeout })._tokenRefreshInterval = checkInterval;
	
	console.log("[Refresh Token Timer] Token refresh timer started");
}

export function validateSiteUrl(url: string, strict = true) {
	try {
		const parts = new URL(url);
		if (!strict) return true;
		return !(
			parts.search ||
			parts.protocol !== "https:"
		);
	} catch (_) {
		return false;
	}
}

export function exampleUrl() {
	const url = new URL(document.URL);
	const parts = url.host.split("iress.");
	return `https://sitename.xplan.iress.${parts[1] || "co.za"}`;
}

export function AddAuthCodeToCookie(authCode: string): void {
	let cookie = getXplanCookie();
	if (!cookie) cookie = "";
	if (cookie.includes("authCode=")) {
		cookie = cookie.substring(0, cookie.indexOf("authCode="));
	}
	cookie = `${cookie}authCode=${authCode}`;
	setXplanCookie(cookie);
}

export function clearLogin(): void {
	// Stop token refresh timer if running
	const refreshInterval = (window as Window & { _tokenRefreshInterval?: NodeJS.Timeout })._tokenRefreshInterval;
	if (refreshInterval) {
		clearInterval(refreshInterval);
		delete (window as Window & { _tokenRefreshInterval?: NodeJS.Timeout })._tokenRefreshInterval;
	}
	
	localStorage.removeItem("loginUserId");
	localStorage.removeItem("loginUserName");
	localStorage.removeItem("xplan_cookie");
	localStorage.removeItem("access_token");
	localStorage.removeItem("refresh_token");
	localStorage.removeItem("auth_token_type");
	localStorage.removeItem("access_token_expiry");
	localStorage.removeItem("expires_in");
	localStorage.removeItem("capabilities_results");
	localStorage.removeItem("entity_entity_client_status_default");
	localStorage.removeItem("entity_note_type_default");
	localStorage.removeItem("entity_note_subtype_depend_default");
	localStorage.removeItem("Permission_default");
	localStorage.removeItem("task_type_default");
	localStorage.removeItem("task_subtype_depend_default");
	localStorage.removeItem("task_status_default");
	localStorage.removeItem("skip_summary");
	localStorage.removeItem("hide_postal_code");
	localStorage.removeItem("hide_dob");
}

export function getUserStorySessionGuid(): string {
	const stored = localStorage.getItem("user_story_session_guid");
	// Check for null, undefined string, empty string, or whitespace
	if (!stored || stored === "undefined" || stored === "null" || stored.trim() === "") {
		const newGuid = uuidv4();
		console.log(`[Session GUID] Generated NEW GUID: ${newGuid}`);
		localStorage.setItem("user_story_session_guid", newGuid);
		return newGuid;
	}
	console.log(`[Session GUID] Retrieved existing GUID from localStorage: ${stored}`);
	return stored;
}

export function getDeploymentId(env = "production") {
	switch (env) {
	case "production":
		return "AKfycbw5zhAB3Ylcky5NBi48YKqtmMHNzJTG_CrdH4mcTEBYMFqltxEEdcGfm4nENnVFUF7J";
	case "staging":
		return "AKfycbycp6Ugj6UH5-FolkQeQVKGG_DA6NZkq0fntnpgSaOPyY0EXQw0URtG7I2NamNoU92KDw";
	default:
		return "";
	}
}

export function isNetworkError(err: any) {
	return !!err.isAxiosError && !err.response;
}

export function handleAxiosError(error: AxiosError) {
	if (!error.isAxiosError) throw new Error("Expected an axios error object");
	return error.response.data;
}

export function updateCredentials(response: AxiosResponse<any>, data: any) {
	const cookie = response?.data?.xplan_cookie;
	if (cookie) {
		const new_cookie = setALBXplanCookie(cookie);
		if (data?.entity_id) localStorage.setItem("loginUserId", data.entity_id);
		if (data?.entity_name) localStorage.setItem("xplan_user_name", data.entity_name);
		setXplanCookie(new_cookie);
	}
}

export function setEmailAddress(email: string) {
	localStorage.setItem("emailAddress", email);
}

export function getEmailAddress() {
	return localStorage.getItem("emailAddress");
}

export function setGraphAccessToken(accessToken: string) {
	if (!accessToken) return;

	const date = new Date();
	date.setMinutes(date.getMinutes() + 55);

	const token = {
		account: Office.context.mailbox.userProfile.emailAddress,
		accessToken,
		ttl: date.getTime(),
	};

	localStorage.setItem("msalAccessToken", JSON.stringify(token));
}

export function getGraphAccessToken() {
	try {
		const token = JSON.parse(localStorage.getItem("msalAccessToken"));

		if (
			!token ||
			Office.context.mailbox.userProfile.emailAddress !== token.account ||
			token.ttl < Date.now()
		) {
			localStorage.removeItem("msalAccessToken");
			return null;
		}

		return token.accessToken;
	} catch {
		return null;
	}
}

export function cleanup() {
	localStorage.clear();
	sessionStorage.clear();
	location.reload();
}
